import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule, Pencil, Plus, RefreshCw, Search, ShieldCheck, UserRound, X } from 'lucide-angular';
import { finalize } from 'rxjs';
import { AdminApiError } from '../core/admin-api';
import { AdminAuthService } from '../core/admin-auth.service';
import { adminUserFormError, AdminUserFormValue } from '../core/admin-user-form';
import { AdminUser } from '../core/models';
import { AdminApiService } from '../data-access/admin-api.service';

@Component({
  selector: 'app-admin-users',
  standalone: true,
  imports: [FormsModule, LucideAngularModule],
  template: `
    <section class="page-heading"><div><span class="eyebrow">ACCESS CONTROL</span><h1>管理者白名單</h1><p>管理可登入後台的帳號、角色與啟用狀態。</p></div><button class="button primary" (click)="openCreate()"><lucide-angular [img]="Plus" />新增管理者</button></section>
    <section class="security-banner"><lucide-angular [img]="ShieldCheck" /><div><strong>Cloudflare Access 與系統白名單同步</strong><p>啟用或停用帳號時，後端會同步 Cloudflare Access；同步失敗不會顯示為成功。</p></div></section>
    <section class="data-card">
      <div class="card-toolbar user-toolbar"><div><h2>管理者帳號</h2><span>共 {{ filteredUsers().length }} 位</span></div><label class="toolbar-search"><lucide-angular [img]="Search" /><input [(ngModel)]="keyword" placeholder="搜尋姓名或 Email"></label></div>
      @if (loading()) {
        <div class="empty-state"><span>…</span><h3>正在載入管理者</h3><p>請稍候。</p></div>
      } @else if (error()) {
        <div class="empty-state"><span>!</span><h3>管理者清單載入失敗</h3><p>{{ error() }}</p><button class="button secondary" (click)="loadUsers()"><lucide-angular [img]="RefreshCw" />重新載入</button></div>
      } @else if (filteredUsers().length) {
        <div class="desktop-table"><table><thead><tr><th>管理者</th><th>角色</th><th>帳號狀態</th><th>建立資訊</th><th>最後更新</th><th></th></tr></thead><tbody>
          @for (user of filteredUsers(); track user.id) {
            <tr><td><div class="admin-cell"><span>{{ user.name.slice(0, 1) }}</span><div><strong>{{ user.name }}</strong><small>{{ user.email }}</small></div></div></td><td><span class="role-badge" [attr.data-role]="user.permissions">{{ user.permissions }}</span></td><td><span class="status-badge" [attr.data-status]="user.status">{{ user.status === 'ACTIVE' ? '啟用中' : '已停用' }}</span></td><td><strong class="table-primary">{{ user.createdAt }}</strong><small>{{ user.createdBy }}</small></td><td><strong class="table-primary">{{ user.updatedAt }}</strong><small>{{ user.updatedBy }}</small></td><td><button class="row-action" (click)="openEdit(user)" [attr.aria-label]="'編輯 ' + user.name"><lucide-angular [img]="Pencil" /></button></td></tr>
          }
        </tbody></table></div>
        <div class="mobile-list">@for (user of filteredUsers(); track user.id) { <button class="admin-mobile" (click)="openEdit(user)"><span class="admin-avatar">{{ user.name.slice(0, 1) }}</span><div><h3>{{ user.name }}</h3><p>{{ user.email }}</p><span class="role-badge" [attr.data-role]="user.permissions">{{ user.permissions }}</span><span class="status-badge" [attr.data-status]="user.status">{{ user.status === 'ACTIVE' ? '啟用中' : '已停用' }}</span></div><lucide-angular [img]="Pencil" /></button> }</div>
      } @else {
        <div class="empty-state"><span>0</span><h3>沒有管理者資料</h3><p>目前白名單沒有可顯示的帳號。</p></div>
      }
    </section>

    @if (formOpen()) {
      <div class="modal-layer" role="dialog" aria-modal="true" aria-labelledby="admin-form-title"><button class="modal-backdrop" aria-label="關閉" (click)="closeForm()"></button><section class="modal-card form-modal">
        <div class="modal-icon neutral"><lucide-angular [img]="UserRound" /></div><button class="modal-close" aria-label="關閉" [disabled]="saving()" (click)="closeForm()"><lucide-angular [img]="X" /></button>
        <h2 id="admin-form-title">{{ form.id ? '編輯管理者' : '新增管理者' }}</h2><p>{{ form.id ? 'Email 不可修改；可調整姓名、角色與啟用狀態。' : '建立可登入 CrewJam 後台的管理者。' }}</p>
        <div class="form-grid">
          <label class="field"><span>中文姓名 <b>*</b></span><input [(ngModel)]="form.name" maxlength="100" placeholder="例如：王小明"></label>
          <label class="field"><span>Email <b>*</b></span><input type="email" [(ngModel)]="form.email" maxlength="254" [disabled]="!!form.id" placeholder="name@example.com"></label>
          <label class="field"><span>角色權限 <b>*</b></span><select [(ngModel)]="form.permissions"><option value="ROOT">ROOT · 完整權限</option><option value="EDITOR">EDITOR · 可查詢與刪除</option><option value="VIEWER">VIEWER · 僅可查詢</option></select></label>
          <label class="field"><span>帳號狀態 <b>*</b></span><select [(ngModel)]="form.status"><option value="ACTIVE">ACTIVE · 啟用</option><option value="DISABLED">DISABLED · 停用</option></select></label>
        </div>
        @if (formError()) { <p class="form-error">{{ formError() }}</p> }
        <div class="modal-actions"><button class="button secondary" [disabled]="saving()" (click)="closeForm()">取消</button><button class="button primary" [disabled]="saving()" (click)="save()">{{ saving() ? '儲存中…' : (form.id ? '儲存變更' : '建立管理者') }}</button></div>
      </section></div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminUsersComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  readonly auth = inject(AdminAuthService);
  readonly users = signal<AdminUser[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly formOpen = signal(false);
  readonly formError = signal('');
  readonly saving = signal(false);
  keyword = '';
  form: AdminUserFormValue = this.emptyForm();
  readonly Plus = Plus; readonly Search = Search; readonly ShieldCheck = ShieldCheck; readonly Pencil = Pencil; readonly UserRound = UserRound; readonly X = X; readonly RefreshCw = RefreshCw;

  ngOnInit(): void { this.loadUsers(); }

  loadUsers(): void {
    if (this.loading()) return;
    this.loading.set(true); this.error.set('');
    this.api.getAdminUsers().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: users => this.users.set(users),
      error: (error: AdminApiError) => this.error.set(error.message),
    });
  }

  filteredUsers(): AdminUser[] { const key = this.keyword.trim().toLowerCase(); return this.users().filter(user => !key || `${user.name} ${user.email} ${user.permissions}`.toLowerCase().includes(key)); }
  openCreate(): void { this.form = this.emptyForm(); this.formError.set(''); this.formOpen.set(true); }
  openEdit(user: AdminUser): void { this.form = { id: user.id, email: user.email, name: user.name, status: user.status, permissions: user.permissions }; this.formError.set(''); this.formOpen.set(true); }
  closeForm(): void { if (!this.saving()) this.formOpen.set(false); }
  save(): void {
    const currentAdminId = this.auth.currentAdmin()?.adminId;
    const validationError = adminUserFormError(this.form, currentAdminId);
    if (validationError) { this.formError.set(validationError); return; }
    if (this.saving()) return;
    const normalized = { email: this.form.email.trim().toLowerCase(), name: this.form.name.trim(), status: this.form.status, permissions: this.form.permissions };
    const request = this.form.id
      ? this.api.updateAdminUser(this.form.id, { name: normalized.name, status: normalized.status, permissions: normalized.permissions })
      : this.api.createAdminUser(normalized);
    this.saving.set(true); this.formError.set('');
    request.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: () => { this.formOpen.set(false); this.loadUsers(); },
      error: (error: AdminApiError) => this.formError.set(error.message),
    });
  }

  private emptyForm(): AdminUserFormValue { return { email: '', name: '', status: 'ACTIVE', permissions: 'EDITOR' }; }
}
